/* =============================================================================
 * M.M.LaFleur — collection card colour swatches.
 *
 * Puts the colour family back on the collection card and lets the shopper flip
 * between colourways in place, with the current colour always named beside the
 * dots.
 *
 * WHY THIS IS CLIENT-SIDE
 * -----------------------
 * mm_collection.liquid can only see the products on the page it is rendering,
 * so the `colors` string it passes to the card describes colourways present in
 * THAT collection. On a seasonal edit carrying one colourway per style —
 * Pre-Fall being exactly that — every group holds a single colour and the row
 * suppressed itself. Liquid has no way to reach the rest of the family: the
 * sibling handles are not known ahead of time and all_products needs a handle.
 *
 * So each card publishes its group keys and the family is resolved from the
 * same endpoint the PDP swatch row uses. Requests are deduplicated by group
 * and deferred until the card nears the viewport, so a 24-card grid does not
 * fire 24 requests on load, and two cards from one style share a single one.
 *
 * SWATCH FILL AND ORDER
 * ---------------------
 * Flat colour from the theme-level map, not the product photograph. A
 * photograph shrunk to 10px is mostly background and model, so a rail of them
 * reads as a row of identical beige smudges rather than as colours.
 *
 * The map's line order is also the swatch order, matching the PDP row. The
 * endpoint returns whatever the search ranked first, which differs per query,
 * so without this the same style showed its colours in a different sequence on
 * every card and again on the PDP.
 * ========================================================================== */

(function () {
  "use strict";

  /* Platter re-injects the footer's scripts around a Barba container swap. */
  if (window.__mmCardSwatchesBound) return;
  window.__mmCardSwatchesBound = true;

  var MAX_DOTS = 8;
  var ROOT_MARGIN = "300px 0px";
  var UNLISTED = 10000;

  /* group key -> Promise of colour list. Shared across every card in the
     group and across the whole session. */
  var GROUPS = {};

  var observer = null;
  var pending = null;
  var COLOR_MAP = null;
  var COLOR_ORDER = null;

  /* Published once per page by mm_footer.liquid from the theme setting, rather
     than repeated into all 24 cards. */
  function parseMap() {
    if (COLOR_MAP) return;
    COLOR_MAP = {};
    COLOR_ORDER = {};
    var seq = 0;
    var raw = window.__mmColorMap || "";
    raw.split("\n").forEach(function (line) {
      var bits = line.split("|");
      if (bits.length < 2) return;
      var key = bits[0].trim().toLowerCase();
      if (!key) return;
      COLOR_MAP[key] = bits[1].trim();
      if (!(key in COLOR_ORDER)) { COLOR_ORDER[key] = seq; seq += 1; }
    });
  }

  function fetchGroup(key) {
    var url = "/search?type=product&view=mm-swatches&q=" + encodeURIComponent(key);
    return fetch(url, { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (data) { return (data && data.colors) || []; })
      .catch(function () { return []; });
  }

  /* Both keys are asked and merged, for the reason documented in
     mm-editorial.js: the YGroup tag is applied to the wrong set on at least
     the Moreland, and the title base misses deliberate groupings that span two
     names. The union is never worse than either alone. */
  function resolve(row) {
    var base = (row.getAttribute("data-group") || "").trim();
    var ygroup = (row.getAttribute("data-ygroup") || "").trim();

    var keys = [];
    if (ygroup) keys.push(ygroup);
    if (base && keys.indexOf(base) === -1) keys.push(base);
    if (!keys.length) return Promise.resolve([]);

    var cacheKey = keys.join("|");
    if (GROUPS[cacheKey]) return GROUPS[cacheKey];

    GROUPS[cacheKey] = Promise.all(keys.map(fetchGroup)).then(function (sets) {
      var merged = [];
      sets.forEach(function (s) { merged = merged.concat(s || []); });
      return dedupe(merged);
    });

    return GROUPS[cacheKey];
  }

  /* The same colourway arrives from both keys, and the catalogue also carries
     separate extended-size records for some colours. Merge on name. */
  function dedupe(colors) {
    var byName = {};
    var order = [];

    colors.forEach(function (c) {
      if (!c || !c.name) return;
      var key = c.name.toLowerCase();
      var seen = byName[key];
      if (!seen) {
        byName[key] = {
          name: c.name,
          url: c.url,
          image: c.image,
          image_lg: c.image_lg,
          available: c.available !== false
        };
        order.push(key);
        return;
      }
      if (c.url && c.url === seen.url) return;
      if (c.available !== false) {
        seen.available = true;
        if (!seen.url) seen.url = c.url;
      }
      if (!seen.image && c.image) seen.image = c.image;
      if (!seen.image_lg && c.image_lg) seen.image_lg = c.image_lg;
    });

    return order.map(function (k) { return byName[k]; });
  }

  /* Canonical order, shared with the PDP row: the colour map's line order,
     then anything not in the map alphabetically after it. */
  function sortByMap(list) {
    parseMap();
    return list.slice().sort(function (a, b) {
      var ai = COLOR_ORDER[a.name.toLowerCase()];
      var bi = COLOR_ORDER[b.name.toLowerCase()];
      if (ai === undefined) ai = UNLISTED;
      if (bi === undefined) bi = UNLISTED;
      if (ai !== bi) return ai - bi;
      return a.name.localeCompare(b.name);
    });
  }

  function setLabel(row, text) {
    var label = row.querySelector("[data-mm-card-color]");
    if (label) label.textContent = text || "";
  }

  function paint(dot, colour) {
    parseMap();
    var hex = COLOR_MAP[colour.name.toLowerCase()];
    if (hex) {
      dot.style.backgroundColor = hex;
      dot.style.backgroundImage = "";
    } else if (colour.image) {
      dot.style.backgroundImage = 'url("' + colour.image + '")';
    } else {
      dot.style.backgroundColor = "#d8d2c8";
    }
  }

  function select(row, colour, dots) {
    var card = row.closest(".mm-card");
    if (!card) return;

    /* Repoint every link on the card so the name, the image and Quick Add all
       lead to the colour now being shown. */
    Array.prototype.forEach.call(card.querySelectorAll("[data-mm-card-link]"), function (a) {
      if (colour.url) a.setAttribute("href", colour.url);
    });

    var quick = card.querySelector("[data-mm-quickadd]");
    if (quick && colour.url) {
      var handle = colour.url.split("/products/").pop().split("?")[0];
      if (handle) quick.setAttribute("data-mm-quickadd", handle);
    }

    var media = card.querySelector(".mm-card-media");
    if (media && (colour.image_lg || colour.image)) {
      var slides = media.querySelectorAll("[data-mm-card-slide]");
      Array.prototype.forEach.call(slides, function (s, i) {
        s.setAttribute("data-active", String(i === 0));
        if (i > 0) s.style.display = "none";
      });
      var first = slides[0];
      if (first) {
        first.style.display = "";
        var img = first.querySelector("img");
        if (img) {
          img.removeAttribute("srcset");
          img.setAttribute("src", colour.image_lg || colour.image);
          img.setAttribute("alt", colour.name);
        }
      }
      /* Only one frame is loaded for a swapped colour, so the carousel arrows
         would step through nothing. */
      var arrows = card.querySelector(".mm-card-arrows");
      if (arrows) arrows.style.display = "none";
    }

    row.setAttribute("data-current", colour.name);
    setLabel(row, colour.name);

    Array.prototype.forEach.call(dots, function (d) {
      var on = d.getAttribute("data-colour") === colour.name;
      d.setAttribute("aria-pressed", String(on));
    });
  }

  function build(row, colors) {
    var wrap = row.querySelector("[data-mm-card-dots]");
    if (!wrap) return;

    var live = colors.filter(function (c) { return c.available !== false; });

    /* A single-colour style still gets its dot. It is not redundant with the
       name beside it — the pair is what tells the shopper this style comes in
       one colour, and it keeps the row visually aligned across the grid. */
    if (!live.length) {
      var current = row.getAttribute("data-current");
      if (!current) return;
      live = [{ name: current, url: "", image: "", available: true }];
    }

    live = sortByMap(live);

    var currentKey = (row.getAttribute("data-current") || "").toLowerCase();
    wrap.innerHTML = "";

    var dots = [];

    live.slice(0, MAX_DOTS).forEach(function (c) {
      var dot = document.createElement("button");
      dot.type = "button";
      dot.className = "mm-dot";
      dot.setAttribute("data-colour", c.name);
      dot.setAttribute("title", c.name);
      dot.setAttribute("aria-label", c.name);
      dot.setAttribute("aria-pressed", String(c.name.toLowerCase() === currentKey));
      paint(dot, c);

      /* Hovering previews the name without committing to the colour, so a
         shopper can read the range without clicking through it. */
      dot.addEventListener("mouseenter", function () { setLabel(row, c.name); });
      dot.addEventListener("mouseleave", function () {
        setLabel(row, row.getAttribute("data-current"));
      });
      dot.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (!c.url) return;
        select(row, c, dots);
      });

      dots.push(dot);
      wrap.appendChild(dot);
    });

    if (live.length > MAX_DOTS) {
      var more = document.createElement("span");
      more.className = "mm-count mm-card-more";
      more.textContent = "+" + (live.length - MAX_DOTS);
      wrap.appendChild(more);
    }
  }

  function hydrate(row) {
    if (row.getAttribute("data-mm-done") === "1") return;
    row.setAttribute("data-mm-done", "1");

    resolve(row).then(function (colors) {
      build(row, colors || []);
    });
  }

  function init() {
    pending = null;

    var rows = document.querySelectorAll("[data-mm-card-swatches]:not([data-mm-done])");
    if (!rows.length) return;

    if (!("IntersectionObserver" in window)) {
      Array.prototype.forEach.call(rows, hydrate);
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          hydrate(entry.target);
        });
      }, { rootMargin: ROOT_MARGIN });
    }

    Array.prototype.forEach.call(rows, function (row) { observer.observe(row); });
  }

  function schedule() {
    if (pending) return;
    pending = window.requestAnimationFrame(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", schedule);
  window.addEventListener("popstate", schedule);

  /* Covers both Barba container swaps and cards appended by
     mm-infinite-scroll.js, neither of which fires an event this can listen
     for directly. */
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
}());
